/**
 * CampaignList component - displays list of marketing campaigns
 */

import { Link } from 'react-router-dom';
import type { Campaign } from '../../services/marketingApi';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Eye, Send } from 'lucide-react';

interface CampaignListProps {
  campaigns: Campaign[];
  isLoading?: boolean;
  onSend?: (campaignId: string) => void;
}

export function CampaignList({ campaigns, isLoading, onSend }: CampaignListProps) {
  const getStatusBadge = (status: Campaign['status']) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Borrador</Badge>;
      case 'sent':
        return <Badge variant="default">Enviada</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelada</Badge>;
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No hay campañas creadas todavía.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>Tipo de servicio</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Enviados</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaigns.map((campaign) => (
            <TableRow key={campaign.id}>
              <TableCell>
                <Link
                  to={`/marketing/campaigns/${campaign.id}`}
                  className="font-medium hover:underline"
                >
                  {campaign.name}
                </Link>
              </TableCell>
              <TableCell>
                {campaign.service_type?.name || (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>{getStatusBadge(campaign.status)}</TableCell>
              <TableCell className="text-right">{campaign.sent_count}</TableCell>
              <TableCell>
                {campaign.sent_at
                  ? formatDate(campaign.sent_at)
                  : formatDate(campaign.created_at)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="icon" asChild>
                    <Link to={`/marketing/campaigns/${campaign.id}`}>
                      <Eye className="h-4 w-4" />
                      <span className="sr-only">Ver detalles</span>
                    </Link>
                  </Button>
                  {campaign.status === 'draft' && onSend && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onSend(campaign.id)}
                    >
                      <Send className="h-4 w-4" />
                      <span className="sr-only">Enviar campaña</span>
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
